import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { 
  Plus, Trash2, GripVertical, Save, Eye, Settings, Copy, 
  ExternalLink, Lock, X, Sparkles, ArrowRight, CheckSquare,
  Layers, Palette
} from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { fieldTypes, FieldElementsSidebar, BrandingBanner, sidebarStyles } from '../components/FormBuilderComponents';

// Sortable Field Component for form canvas
const SortableField = ({ field, onDelete, isSelected, onSelect }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });
  const fieldType = fieldTypes.find(f => f.type === field.type);
  const Icon = fieldType?.icon;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border-2 rounded-xl p-4 cursor-pointer transition-all ${
        isSelected 
          ? 'border-indigo-500 shadow-lg shadow-indigo-100' 
          : 'border-slate-200 hover:border-indigo-300 hover:shadow-md'
      }`}
      onClick={() => onSelect(field.id)}
    >
      <div className="flex items-start gap-3">
        <div {...attributes} {...listeners} className="cursor-grab mt-1 p-1 hover:bg-slate-100 rounded">
          <GripVertical className="w-4 h-4 text-slate-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {Icon && <Icon className="w-4 h-4 text-indigo-500" />}
            <span className="font-medium text-slate-900 truncate">{field.label}</span>
            {field.required && <span className="text-red-500 text-sm">*</span>}
          </div>
          <p className="text-xs text-slate-500 capitalize">{field.type.replace('_', ' ')}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(field.id);
          }}
          className="text-slate-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export const GuestFormBuilderPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [formTitle, setFormTitle] = useState('My New Form');
  const [formDescription, setFormDescription] = useState('');
  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [promptAction, setPromptAction] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('build');
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Load from localStorage on mount
  useEffect(() => {
    const savedForm = localStorage.getItem('guest_form_draft');
    if (savedForm) {
      try {
        const parsed = JSON.parse(savedForm);
        setFormTitle(parsed.title || 'My New Form');
        setFormDescription(parsed.description || '');
        setFields(parsed.fields || []);
      } catch (e) {
        // Saved form data corrupted, start fresh
      }
    }
    setIsInitialized(true);
  }, []);

  // Auto-save to localStorage
  useEffect(() => {
    if (!isInitialized) return;
    const formData = { title: formTitle, description: formDescription, fields };
    localStorage.setItem('guest_form_draft', JSON.stringify(formData));
  }, [formTitle, formDescription, fields, isInitialized]);

  // Redirect authenticated users
  useEffect(() => {
    if (isAuthenticated) {
      const savedForm = localStorage.getItem('guest_form_draft');
      if (savedForm) {
        try {
          const parsed = JSON.parse(savedForm);
          if (parsed.fields && parsed.fields.length > 0) {
            localStorage.setItem('create_form_from_draft', 'true');
            toast.success('Your form draft is being saved!');
            navigate('/dashboard?import_draft=true');
            return;
          }
        } catch (e) {
          // Draft JSON is corrupted, ignore it
        }
      }
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const addField = (type) => {
    const fieldType = fieldTypes.find(f => f.type === type);
    const newField = {
      id: `field-${Date.now()}`,
      type,
      label: fieldType?.label || 'New Field',
      placeholder: '',
      required: false,
      options: ['select', 'radio', 'checkbox', 'multiple'].includes(type) 
        ? ['Option 1', 'Option 2', 'Option 3'] 
        : undefined,
    };
    setFields([...fields, newField]);
    setSelectedField(newField.id);
    setShowMobileSidebar(false);
  };

  const deleteField = (fieldId) => {
    setFields(fields.filter(f => f.id !== fieldId));
    if (selectedField === fieldId) setSelectedField(null);
  };

  const updateField = (fieldId, updates) => {
    setFields(fields.map(f => f.id === fieldId ? { ...f, ...updates } : f));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = fields.findIndex(f => f.id === active.id);
      const newIndex = fields.findIndex(f => f.id === over.id);
      setFields(arrayMove(fields, oldIndex, newIndex));
    }
  };

  const showSignupRequired = (action) => {
    setPromptAction(action);
    setShowSignupPrompt(true);
  };

  const selectedFieldData = fields.find(f => f.id === selectedField);

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/formlio_final_logo.png" alt="Formlio" className="h-8 w-8 object-contain rounded-md" />
              <span className="text-xl font-bold text-slate-900 hidden sm:block">Formlio</span>
            </Link>
            <div className="h-6 w-px bg-slate-200 hidden sm:block" />
            <span className="text-sm text-slate-600 hidden sm:block truncate max-w-[200px]">
              {formTitle || 'Untitled Form'}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPreview(true)}
              className="hidden sm:flex"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button
              size="sm"
              onClick={() => showSignupRequired('save')}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Save className="w-4 h-4 mr-2" />
              Save
              <Lock className="w-3 h-3 ml-2 opacity-60" />
            </Button>
            <Button asChild size="sm" className="bg-green-600 hover:bg-green-700">
              <Link to="/signup">
                Sign Up Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 pb-3 flex items-center gap-2">
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            {[
              { id: 'build', label: 'Build', icon: Layers },
              { id: 'design', label: 'Design', icon: Palette },
              { id: 'settings', label: 'Settings', icon: Settings },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.id !== 'build') showSignupRequired(`access ${tab.label.toLowerCase()}`);
                  else setActiveTab(tab.id);
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all flex items-center gap-1.5 ${
                  activeTab === tab.id
                    ? 'bg-white text-indigo-600 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.id !== 'build' && <Lock className="w-3 h-3 opacity-50" />}
              </button>
            ))}
          </div>
          
          {/* Mobile add field button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowMobileSidebar(true)}
            className="lg:hidden ml-auto"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Field
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden" style={{ paddingBottom: '100px' }}>
        {/* Left Sidebar - Field Elements (Desktop) */}
        <div className="hidden lg:block flex-shrink-0 h-full overflow-hidden">
          <FieldElementsSidebar 
            onAddField={addField}
            isPremiumUser={false}
          />
        </div>

        {/* Mobile Sidebar */}
        {showMobileSidebar && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowMobileSidebar(false)} />
            <div className="absolute left-0 top-0 h-full">
              <FieldElementsSidebar 
                onAddField={addField}
                onClose={() => setShowMobileSidebar(false)}
                showClose={true}
                isPremiumUser={false}
              />
            </div>
          </div>
        )}

        {/* Center - Form Canvas */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="max-w-2xl mx-auto">
            {/* Form Header */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-4">
              <Input
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                className="text-2xl font-bold border-0 px-0 focus-visible:ring-0 mb-2 h-auto"
                placeholder="Form Title"
              />
              <Input
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                className="text-slate-500 border-0 px-0 focus-visible:ring-0"
                placeholder="Add a description (optional)"
              />
            </div>

            {/* Fields Canvas */}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3">
                  {fields.length === 0 ? (
                    <div className="bg-white rounded-xl border-2 border-dashed border-slate-300 p-12 text-center">
                      <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Plus className="w-8 h-8 text-indigo-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">Start Building Your Form</h3>
                      <p className="text-slate-500 mb-4 text-sm">
                        Click on fields from the sidebar to add them here
                      </p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {fieldTypes.slice(0, 4).map(ft => (
                          <Button 
                            key={ft.type} 
                            variant="outline" 
                            size="sm" 
                            onClick={() => addField(ft.type)}
                            className="text-xs"
                          >
                            <ft.icon className="w-3 h-3 mr-1" />
                            {ft.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    fields.map(field => (
                      <SortableField
                        key={field.id}
                        field={field}
                        onDelete={deleteField}
                        isSelected={selectedField === field.id}
                        onSelect={setSelectedField}
                      />
                    ))
                  )}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>

        {/* Right Sidebar - Field Properties */}
        <div className="hidden md:block w-72 bg-white border-l border-slate-200 overflow-y-auto flex-shrink-0">
          <div className="p-4">
            <h3 className="font-semibold text-slate-900 mb-4 text-sm">Field Properties</h3>
            
            {selectedFieldData ? (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-600">Label</Label>
                  <Input
                    value={selectedFieldData.label}
                    onChange={(e) => updateField(selectedField, { label: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-xs text-slate-600">Placeholder</Label>
                  <Input
                    value={selectedFieldData.placeholder || ''}
                    onChange={(e) => updateField(selectedField, { placeholder: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div className="flex items-center justify-between py-2">
                  <Label className="text-xs text-slate-600">Required</Label>
                  <Switch
                    checked={selectedFieldData.required}
                    onCheckedChange={(checked) => updateField(selectedField, { required: checked })}
                  />
                </div>

                {selectedFieldData.options && (
                  <div>
                    <Label className="text-xs text-slate-600">Options</Label>
                    <div className="space-y-2 mt-2">
                      {(selectedFieldData.options || []).map((option, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input
                            value={option}
                            onChange={(e) => {
                              const newOptions = [...(selectedFieldData.options || [])];
                              newOptions[index] = e.target.value;
                              updateField(selectedField, { options: newOptions });
                            }}
                            placeholder={`Option ${index + 1}`}
                            className="flex-1 h-9 text-sm"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const newOptions = [...(selectedFieldData.options || [])];
                                newOptions.splice(index + 1, 0, '');
                                updateField(selectedField, { options: newOptions });
                              }
                            }}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newOptions = (selectedFieldData.options || []).filter((_, i) => i !== index);
                              updateField(selectedField, { options: newOptions.length > 0 ? newOptions : ['Option 1'] });
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-9 w-9 p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newOptions = [...(selectedFieldData.options || []), `Option ${(selectedFieldData.options || []).length + 1}`];
                          updateField(selectedField, { options: newOptions });
                        }}
                        className="w-full text-indigo-600 border-indigo-200 hover:bg-indigo-50 text-xs"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add Option
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-slate-500 py-8">
                <Settings className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">Select a field to edit</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Branding Banner - Always visible */}
      <BrandingBanner userPlan="free" />

      {/* Signup Prompt Dialog */}
      <Dialog open={showSignupPrompt} onOpenChange={setShowSignupPrompt}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-indigo-600" />
              Create a Free Account
            </DialogTitle>
            <DialogDescription>
              Sign up for free to {promptAction}. Your progress will be saved!
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-indigo-50 rounded-xl p-4 mb-4">
              <h4 className="font-medium text-indigo-900 mb-3">Free account includes:</h4>
              <ul className="space-y-2 text-sm text-indigo-700">
                {['Save & publish up to 5 forms', 'Collect 50 submissions/month', 'All basic field types', 'Email notifications'].map(item => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckSquare className="w-4 h-4" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-3">
              <Button asChild className="w-full bg-indigo-600 hover:bg-indigo-700">
                <Link to="/signup">
                  Sign Up for Free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link to="/login">
                  Already have an account? Login
                </Link>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Form Preview</DialogTitle>
            <DialogDescription>This is how your form will look</DialogDescription>
          </DialogHeader>
          
          <div className="bg-slate-50 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{formTitle}</h2>
            {formDescription && <p className="text-slate-500 mb-6">{formDescription}</p>}
            
            <div className="space-y-6">
              {fields.map(field => (
                <div key={field.id}>
                  <Label className="mb-2 block text-sm font-medium">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  
                  {field.type === 'textarea' ? (
                    <Textarea placeholder={field.placeholder} disabled className="bg-white" />
                  ) : field.type === 'select' ? (
                    <Select disabled>
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder={field.placeholder || 'Select...'} />
                      </SelectTrigger>
                    </Select>
                  ) : ['checkbox', 'multiple'].includes(field.type) ? (
                    <div className="space-y-2">
                      {field.options?.map((opt, i) => (
                        <label key={i} className="flex items-center gap-2">
                          <input type="checkbox" disabled className="rounded" />
                          <span className="text-sm">{opt}</span>
                        </label>
                      ))}
                    </div>
                  ) : field.type === 'radio' ? (
                    <div className="space-y-2">
                      {field.options?.map((opt, i) => (
                        <label key={i} className="flex items-center gap-2">
                          <input type="radio" disabled name={field.id} />
                          <span className="text-sm">{opt}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <Input placeholder={field.placeholder} disabled className="bg-white" />
                  )}
                </div>
              ))}
            </div>

            {fields.length > 0 && (
              <Button className="w-full mt-6 bg-indigo-600" disabled>Submit</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GuestFormBuilderPage;
